import React, { useEffect, useState } from "react";
import { CreditCard, Loader2, CheckCircle2, ExternalLink, AlertCircle, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CardPayments = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [example, setExample] = useState(1000);

  const load = async () => {
    try { const { data } = await api.get("/connect/status"); setStatus(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", {});
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setConnecting(false); }
  };

  if (loading) return null;
  const active = status?.charges_enabled;
  const pending = status?.connected && !active;
  const due = status?.requirements_due || 0;
  const verifying = status?.pending_verification || 0;

  const badge = active
    ? { cls: "bg-green-100 text-green-700", text: "Active" }
    : status?.connected
      ? due > 0
        ? { cls: "bg-amber-100 text-amber-700", text: `Verification pending — ${due} item${due === 1 ? "" : "s"} outstanding` }
        : verifying > 0
          ? { cls: "bg-blue-100 text-blue-700", text: "Under review by Stripe" }
          : { cls: "bg-amber-100 text-amber-700", text: "Setup incomplete" }
      : { cls: "bg-slate-100 text-slate-500", text: "Not connected" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="card-payments-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-slate-400" /> Card payments
        </h2>
        <span className={`text-xs font-semibold rounded-full px-3 py-1 ${badge.cls}`} data-testid="connect-status-badge">{badge.text}</span>
      </div>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Let clients pay your Applications for Payment by card. Powered by Stripe — funds go straight to your bank.
        {status?.platform_fee_pct > 0 && ` A ${status.platform_fee_pct}% platform fee applies.`}
      </p>

      {active ? (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3" data-testid="connect-active">
          <CheckCircle2 className="h-5 w-5" /> Card payments are active. Clients will see a "Pay by card" button on your shared applications.
        </div>
      ) : pending ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="connect-pending">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <span>
              {due > 0
                ? `Stripe still needs ${due} more detail${due === 1 ? "" : "s"} before you can accept card payments. Click below to finish.`
                : verifying > 0
                  ? "Stripe is reviewing your details. This usually takes a few minutes to a couple of days — no action needed right now."
                  : "Your Stripe setup is incomplete. Finish onboarding to start accepting card payments."}
            </span>
          </div>
          {due > 0 && (
            <Button onClick={onboard} disabled={connecting} data-testid="connect-continue-btn" className="bg-blue-600 hover:bg-blue-700">
              {connecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1.5" />} Continue Stripe setup
            </Button>
          )}
        </div>
      ) : (
        <Button onClick={onboard} disabled={connecting} data-testid="connect-start-btn" className="bg-blue-600 hover:bg-blue-700">
          {connecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1.5" />} Connect Stripe to accept cards
        </Button>
      )}

      {/* Net payout preview */}
      {(() => {
        const gross = Math.max(0, Number(example) || 0);
        const feePct = (status?.platform_fee_pct ?? 0) / 100;
        const stripeFee = gross > 0 ? gross * 0.015 + 0.2 : 0;
        const platformFee = gross * feePct;
        const net = Math.max(0, gross - stripeFee - platformFee);
        return (
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="payout-preview">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Calculator className="h-4 w-4 text-slate-400" /> What you'll receive</p>
            <div className="flex items-end gap-3 mt-3">
              <div>
                <Label className="text-xs">Example payment (£)</Label>
                <Input type="number" min={0} value={example} onChange={(e) => setExample(e.target.value)} data-testid="payout-amount-input" className="mt-1 w-32 bg-white" />
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-sm max-w-xs">
              <div className="flex justify-between"><span className="text-slate-500">Client pays</span><span className="font-mono text-slate-800">{gbp(gross)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Est. Stripe fee (1.5% + 20p)</span><span className="font-mono text-red-600">- {gbp(stripeFee)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">TradeOff fee ({status?.platform_fee_pct ?? 0}%)</span><span className="font-mono text-red-600">- {gbp(platformFee)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1.5 font-semibold"><span className="text-slate-700">You receive ≈</span><span className="font-mono text-green-700" data-testid="payout-net">{gbp(net)}</span></div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Estimate only. Stripe's actual fee depends on the card type (UK, EEA or international) — see your Stripe dashboard for exact rates.</p>
          </div>
        );
      })()}
    </div>
  );
};

export default CardPayments;
